import React, { useMemo, useState } from "react";
import { CaretLeft, CaretRight, Play, FilePdf, PencilSimple, Trash, Plus, ShareNetwork } from "@phosphor-icons/react";

// Returns Monday of the week for a given date (Monday=start of week).
function startOfWeek(d) {
  const date = new Date(d);
  date.setHours(0, 0, 0, 0);
  const day = date.getDay(); // 0=Sun, 1=Mon ... 6=Sat
  const diff = day === 0 ? -6 : 1 - day;
  date.setDate(date.getDate() + diff);
  return date;
}

function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

function toISODate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function sameISODate(a, b) {
  return toISODate(a) === toISODate(b);
}

const DAY_LABELS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

export default function WeeklyCalendar({ routines, sessions, isAdmin, onStartLive, onEdit, onDelete, onExportPDF, onShare, onCreateForDate }) {
  const [anchor, setAnchor] = useState(() => new Date());
  const weekStart = useMemo(() => startOfWeek(anchor), [anchor]);
  const weekEnd = useMemo(() => addDays(weekStart, 6), [weekStart]);
  const today = new Date();

  // Group routines by ISO date for O(1) lookup per day
  const routinesByDate = useMemo(() => {
    const map = {};
    for (const r of routines || []) {
      if (!r.date) continue;
      (map[r.date] ||= []).push(r);
    }
    return map;
  }, [routines]);

  // Map of "routine_id -> session" (completed sessions only, most recent first)
  const completedByRoutineId = useMemo(() => {
    const map = {};
    for (const s of sessions || []) {
      if (s.status === "completed" && s.routine_id && !map[s.routine_id]) {
        map[s.routine_id] = s;
      }
    }
    return map;
  }, [sessions]);

  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const totalThisWeek = days.reduce((acc, d) => acc + (routinesByDate[toISODate(d)]?.length || 0), 0);

  const fmtRange = () => {
    const opts = { day: "2-digit", month: "short" };
    return `${weekStart.toLocaleDateString("es-AR", opts)} — ${weekEnd.toLocaleDateString("es-AR", opts)}`;
  };

  return (
    <div className="card-base p-5 sm:p-6" data-testid="weekly-calendar">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5 pb-5 border-b border-app">
        <div>
          <div className="text-xs uppercase tracking-widest text-primary-app mb-1">Semana</div>
          <h3 className="font-display font-black uppercase text-2xl tracking-tighter" data-testid="week-range">{fmtRange()}</h3>
          <div className="text-xs text-muted mt-1">{totalThisWeek} rutina{totalThisWeek === 1 ? "" : "s"} programada{totalThisWeek === 1 ? "" : "s"}</div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setAnchor(addDays(weekStart, -7))}
            className="btn-secondary !py-2 !px-3"
            data-testid="week-prev"
            title="Semana anterior"
          >
            <CaretLeft size={16} weight="bold" />
          </button>
          <button
            onClick={() => setAnchor(new Date())}
            className="btn-secondary !py-2 !px-3 text-xs uppercase tracking-widest"
            data-testid="week-today"
          >
            Hoy
          </button>
          <button
            onClick={() => setAnchor(addDays(weekStart, 7))}
            className="btn-secondary !py-2 !px-3"
            data-testid="week-next"
            title="Semana siguiente"
          >
            <CaretRight size={16} weight="bold" />
          </button>
        </div>
      </div>

      {/* Grid: horizontally scrollable on narrow screens, 7 cols desktop */}
      <div className="grid grid-cols-1 md:grid-cols-7 gap-3">
        {days.map((d, i) => {
          const iso = toISODate(d);
          const items = routinesByDate[iso] || [];
          const isToday = sameISODate(d, today);
          const isPast = d < new Date(toISODate(today));
          return (
            <div
              key={iso}
              className={`border p-3 min-h-[140px] flex flex-col transition-colors ${
                isToday ? "border-[#FF3B30] bg-[#1a0505]" : "border-app bg-[#0A0A0A]"
              }`}
              data-testid={`day-${iso}`}
            >
              <div className="flex items-baseline justify-between mb-3">
                <div>
                  <div className={`text-[10px] uppercase tracking-widest font-bold ${isToday ? "text-primary-app" : "text-muted"}`}>{DAY_LABELS[i]}</div>
                  <div className={`font-display font-black text-2xl leading-none mt-0.5 ${isToday ? "text-white" : isPast ? "text-muted" : "text-white"}`}>{d.getDate()}</div>
                </div>
                {isAdmin && (
                  <button
                    onClick={() => onCreateForDate?.(iso)}
                    className="text-muted hover:text-primary-app transition-colors"
                    data-testid={`add-on-${iso}`}
                    title="Agregar rutina este día"
                  >
                    <Plus size={14} weight="bold" />
                  </button>
                )}
              </div>

              {items.length === 0 ? (
                <div className="text-[10px] text-muted uppercase tracking-widest mt-auto opacity-50">
                  {isPast ? "Sin rutina" : "Libre"}
                </div>
              ) : (
                <div className="space-y-2 flex-1">
                  {items.map((r) => {
                    const done = completedByRoutineId[r.routine_id];
                    return (
                      <div
                        key={r.routine_id}
                        className={`border p-2 ${done ? "border-[#32D74B]/40 bg-[#0e1f0e]" : "border-[#262626] bg-[#141414]"}`}
                        data-testid={`cal-routine-${r.routine_id}`}
                      >
                        <div className="flex items-start gap-1 mb-1">
                          {done && <span className="text-[9px] uppercase tracking-widest font-bold bg-[#32D74B] text-black px-1.5 py-0.5 leading-none">OK</span>}
                          <div className="font-display font-bold uppercase text-xs leading-tight flex-1 truncate" title={r.name}>
                            {r.name}
                          </div>
                        </div>
                        {r.objectives && (
                          <div className="text-[10px] text-muted line-clamp-2 mb-1">{r.objectives}</div>
                        )}
                        <div className="text-[10px] text-muted mb-2">{(r.exercises || []).length} ej.</div>
                        <div className="flex gap-1 flex-wrap">
                          {(r.exercises || []).length > 0 && (
                            <button
                              onClick={() => onStartLive?.(r)}
                              className="text-[10px] uppercase tracking-widest font-bold bg-[#FF3B30] hover:bg-[#FF5A50] text-white px-2 py-1 flex items-center gap-1 transition-colors"
                              data-testid={`cal-live-${r.routine_id}`}
                            >
                              <Play size={10} weight="fill" /> Iniciar
                            </button>
                          )}
                          <button
                            onClick={() => onExportPDF?.(r)}
                            className="text-[10px] font-bold border border-[#262626] hover:border-white text-muted hover:text-white px-1.5 py-1 transition-colors"
                            title="PDF"
                            data-testid={`cal-pdf-${r.routine_id}`}
                          >
                            <FilePdf size={10} weight="bold" />
                          </button>
                          <button
                            onClick={() => onShare?.(r)}
                            className={`text-[10px] font-bold border px-1.5 py-1 transition-colors ${r.is_shared ? "border-[#32D74B] text-[#32D74B]" : "border-[#262626] text-muted hover:border-white hover:text-white"}`}
                            title={r.is_shared ? "Compartido" : "Compartir"}
                            data-testid={`cal-share-${r.routine_id}`}
                          >
                            <ShareNetwork size={10} weight="bold" />
                          </button>
                          {isAdmin && (
                            <>
                              <button
                                onClick={() => onEdit?.(r)}
                                className="text-[10px] font-bold border border-[#262626] hover:border-white text-muted hover:text-white px-1.5 py-1 transition-colors"
                                title="Editar"
                                data-testid={`cal-edit-${r.routine_id}`}
                              >
                                <PencilSimple size={10} weight="bold" />
                              </button>
                              <button
                                onClick={() => onDelete?.(r.routine_id)}
                                className="text-[10px] font-bold border border-[#262626] hover:border-[#FF3B30] text-muted hover:text-[#FF3B30] px-1.5 py-1 transition-colors"
                                title="Eliminar"
                                data-testid={`cal-delete-${r.routine_id}`}
                              >
                                <Trash size={10} weight="bold" />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
